import { FC, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useGetDeviceEventsListQuery } from "../../../api/network/camerasAdminVMApiService";
import { useGetEventCommentsQuery } from "../../../api/network/vmApiService";
import { APP_CONFIG } from "../../../constants/constants";
import { useAccountTimezone } from "../../../hooks/timezone";
import {
  dbTimeToJsTime,
  formattedDateTimeBasedOnTimezone,
  getStartEndOfDayInUTC,
  humanReadableTimeDuration,
  jsTimeToDBTime,
} from "../../../utils/dateTime";
import { convertKmphTo } from "../../../utils/distance";
import { useLoggedInUserData } from "../../../utils/user";
import { SPEED_UNIT_VALUES, SPEED_UNITS_OPTIONS } from "../../admin-details/account/constants";
import { IGNORED_EVENTS, RENAMED_EVENTS } from "../../camera-admin/constants";
import { IncidentDetailResponse } from "./type";

const NOT_AVAILABLE = "Not Available";

const EventSpecItem = ({ title, value }: { title: string; value: string }) => (
  <div className="col-span-6 md:col-span-4 flex flex-col gap-1">
    <span className="text-xs text-heading-gray666">{title}</span>
    <span className="text-sm font-medium text-heading-black break-words">{value}</span>
  </div>
);

type IncidentDetailEventDetailsProps = {
  incident: IncidentDetailResponse;
  printMode?: boolean;
  onLoadComplete?: () => void;
};

const IncidentDetailEventDetails: FC<IncidentDetailEventDetailsProps> = (
  { incident, printMode=false, onLoadComplete },
 ) => {
  const { t } = useTranslation("translation", {
    keyPrefix: "admins.incident.incident_review.detailsPage.widget_event_details",
  });
  const { convertToTZForDateFns, tzForDateFns } = useAccountTimezone();
  const hasCalledLoadComplete = useRef(false);
  const orgSpeedUnit = useLoggedInUserData("speedUnits") as SPEED_UNIT_VALUES;
  const speedOption =
    SPEED_UNITS_OPTIONS.find((option) => option.value === orgSpeedUnit) ?? SPEED_UNITS_OPTIONS[0];

  const formatSpeed = (speedKph: number) =>
    isNaN(speedKph) || speedKph < 0
      ? NOT_AVAILABLE
      : `${Math.round(convertKmphTo(speedOption.unit, speedKph))} ${speedOption.label}`;

  const { data: dataEventComments, isFetching: isFetchingEventComments } = useGetEventCommentsQuery(
    incident.video_ticket || "",
    { skip: !incident.video_ticket }
  );
  const coachingHistoryValue = !incident.video_ticket
    ? NOT_AVAILABLE
    : isFetchingEventComments
      ? "..."
      : (dataEventComments?.data?.length || 0) > 0
        ? `${dataEventComments?.data.length} comments found`
        : "None";

  const incidentDateInTimezone = formattedDateTimeBasedOnTimezone(
    incident.timestamp * 1000,
    tzForDateFns,
    "yyyy-MM-dd"
  );
  const { startOfDayInUTC } = getStartEndOfDayInUTC(incidentDateInTimezone, tzForDateFns);
  const { data: dataPriorEvents, isFetching: isFetchingPriorEvents } = useGetDeviceEventsListQuery(
    {
      serial_number: incident.camera_id || "",
      start_time: jsTimeToDBTime(startOfDayInUTC.getTime()),
      end_time: incident.timestamp - 1,
      page: 1,
      page_size: 25,
    },
    { skip: !incident.camera_id }
  );
  const filteredADASAIEvents = (dataPriorEvents?.results || []).filter(
    (item) => !IGNORED_EVENTS.includes(item.status.description)
  );
  const adasAlertsValue = !incident.camera_id
    ? NOT_AVAILABLE
    : isFetchingPriorEvents
      ? "..."
      : filteredADASAIEvents.length > 0
        ? filteredADASAIEvents
            .reduce<string[]>(
              (acc, item) => (acc.includes(item.status.description) ? acc : [...acc, item.status.description]),
              []
            )
            .map((item) => RENAMED_EVENTS.find((renamed) => renamed.backendValue === item)?.displayValue || item)
            .join(", ")
        : NOT_AVAILABLE;
        
   useEffect(() => {
    if ( printMode && !isFetchingPriorEvents && !hasCalledLoadComplete.current
    ) {
      onLoadComplete?.();
      if (APP_CONFIG.DEBUG.ALL)
        console.log(
          "===== INCIDENT DETAIL EVENT DETAILS LOADED",
          new Date().getTime()
        );
      hasCalledLoadComplete.current = true;
    }
  }, [printMode, isFetchingPriorEvents, onLoadComplete]);

  return (
    <div className="break-before-page flex flex-col bg-white rounded-xl border border-heading-gray888/20 shadow-sm p-4">
      <div className="flex items-center justify-between mb-4">
       <h2 className={`font-semibold text-heading-black ${printMode ? "text-xl" : "text-[15px]"}`}>{t("heading")}</h2>
      </div>
      <div className="grid grid-cols-12 gap-x-4 gap-y-4">
        <h4 className="col-span-12 text-sm font-bold text-heading-black">{t("section_vehicle_driver")}</h4>
        <EventSpecItem title={t("field_asset_id")} value={incident.deviceid || NOT_AVAILABLE} />
        <EventSpecItem title={t("field_asset_name")} value={incident.asset_name || NOT_AVAILABLE} />
        <EventSpecItem title={t("field_speed")} value={formatSpeed(incident.speedkph)} />
        <EventSpecItem title={t("field_speed_limit")} value={formatSpeed(incident.speedlimitkph)} />

        <hr className="col-span-12 border-heading-gray888/15" />
        <h4 className="col-span-12 text-sm font-bold text-heading-black">{t("section_event_metadata")}</h4>
        <EventSpecItem
          title={t("field_event_date")}
          value={
            incident.timestamp
              ? formattedDateTimeBasedOnTimezone(
                  dbTimeToJsTime(incident.timestamp),
                  convertToTZForDateFns(tzForDateFns),
                  APP_CONFIG.DATETIME_FORMATS.DATEPICKER.DATE_ONLY
                )
              : NOT_AVAILABLE
          }
        />
        <EventSpecItem
          title={t("field_event_time")}
          value={
            incident.timestamp
              ? formattedDateTimeBasedOnTimezone(
                  dbTimeToJsTime(incident.timestamp),
                  convertToTZForDateFns(tzForDateFns),
                  APP_CONFIG.DATETIME_FORMATS.DATEPICKER.TIME_ONLY
                )
              : NOT_AVAILABLE
          }
        />
        <EventSpecItem title={t("field_gps_location")} value={incident.address || NOT_AVAILABLE} />
        <EventSpecItem title={t("field_event_type")} value={incident.incident_type || NOT_AVAILABLE} />

        <hr className="col-span-12 border-heading-gray888/15" />
        <h4 className="col-span-12 text-sm font-bold text-heading-black">{t("section_driver_context")}</h4>
        <EventSpecItem
          title={t("field_driver_identity")}
          value={incident.driver_name || incident.driverid || NOT_AVAILABLE}
        />
        <EventSpecItem
          title={t("field_driving_hours")}
          value={
            incident.driving_time_in_sec
              ? humanReadableTimeDuration(incident.driving_time_in_sec * 1000, false)
              : NOT_AVAILABLE
          }
        />
        <EventSpecItem title={t("field_coaching_history")} value={coachingHistoryValue} />

        <hr className="col-span-12 border-heading-gray888/15" />
        <h4 className="col-span-12 text-sm font-bold text-heading-black">{t("section_violations")}</h4>
        <EventSpecItem title={t("field_adas_alerts")} value={adasAlertsValue} />
        {/* Manual Triggers removed - no backing data source exists. The source Event
        Details component (video-history-event-details.tsx) already flags this same
        field as dead code and omits it, for the same reason. */}
      </div>
    </div>
  );
};

export default IncidentDetailEventDetails;
